import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import LineItemsEditor, { computeTotals } from "@/components/LineItemsEditor";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";
import { ArrowLeft, FloppyDisk, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);

export default function QuotationEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeCompanyId, companies, isGstCompany, dateFloor } = useAuth();
  const isEdit = !!id;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [date, setDate] = useState(today());
  const [validTill, setValidTill] = useState("");
  const [status, setStatus] = useState("draft");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [shareToken, setShareToken] = useState("");

  useEffect(() => {
    (async () => {
      const [cs, ps, sv] = await Promise.all([api.get("/customers"), api.get("/products"), api.get("/services").catch(() => ({ data: [] }))]);
      setCustomers(cs.data);
      const items = [
        ...ps.data.map((p) => ({ ...p, _kind: "product" })),
        ...(sv.data || []).map((s) => ({
          id: s.id, name: `${s.name} (Service)`, hsn: s.hsn_code || "",
          unit: "SRV", sale_price: s.price || 0, tax_rate: s.tax_rate ?? 18, _kind: "service",
        })),
      ];
      setProducts(items);
      if (isEdit) {
        try {
          const { data } = await api.get(`/quotations/${id}`);
          setDate(data.date); setValidTill(data.valid_till || "");
          setStatus(data.status); setNotes(data.notes || ""); setItems(data.items || []);
          setShareToken(data.share_token || "");
          const cust = cs.data.find((c) => c.id === data.customer_id);
          setCustomer(cust || { id: data.customer_id, name: data.customer_name, state_code: data.customer_state_code });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
      }
    })();
  }, [activeCompanyId, id, isEdit]);

  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const intraState = customer && activeCompany && customer.state_code && activeCompany.state_code && customer.state_code === activeCompany.state_code;
  const totals = useMemo(() => computeTotals(items, !!intraState, isGstCompany), [items, intraState, isGstCompany]);

  const save = async () => {
    if (!customer) { toast.error("Select a customer"); return; }
    if (items.length === 0) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      const payload = {
        date, valid_till: validTill, status, notes,
        customer_id: customer.id, customer_name: customer.name,
        customer_state_code: customer.state_code || "",
        items: items.map((it) => ({
          product_id: it.product_id || "", name: it.name, hsn: it.hsn || "",
          quantity: Number(it.quantity) || 0, unit: it.unit || "PCS",
          rate: Number(it.rate) || 0, discount_pct: Number(it.discount_pct) || 0,
          tax_rate: Number(it.tax_rate) || 0,
        })),
      };
      if (isEdit) {
        await api.patch(`/quotations/${id}`, payload);
        toast.success("Quotation updated");
      } else {
        await api.post("/quotations", payload);
        toast.success("Quotation created");
      }
      navigate("/quotations");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="page-shell text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <div>
            <div className="eyebrow">Sales</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{isEdit ? "Edit Quotation" : "New Quotation"}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && shareToken && (
            <Button
              variant="outline"
              onClick={() => {
                const w = window.open(
                  `/p/quotation/${id}?t=${shareToken}&autoprint=1`,
                  "_blank",
                  "noopener,noreferrer"
                );
                if (!w) toast.error("Please allow pop-ups to print");
              }}
              className="h-9"
              data-testid="quot-print-btn"
            >
              <Printer size={16} className="mr-1.5" /> Print
            </Button>
          )}
        </div>
      </div>

      <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-1.5 md:col-span-2">
          <Label className="eyebrow">Customer *</Label>
          <SearchableCustomerSelect
            value={customer?.id || ""}
            onChange={(v) => setCustomer(customers.find((c) => c.id === v))}
            customers={customers}
            placeholder="Search & choose customer"
            testid="quot-customer"
            getSubtitle={(c) => c.gstin || c.phone}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Date</Label>
          <Input type="date" min={dateFloor} value={date} onChange={(e) => setDate(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Valid Till</Label>
          <Input type="date" value={validTill} onChange={(e) => setValidTill(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-3">
          <Label className="eyebrow">Notes / Work Name</Label>
          <Textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Terms & conditions, delivery notes…" />
        </div>
      </div>

      <LineItemsEditor items={items} setItems={setItems} products={products} intraState={!!intraState} gstEnabled={isGstCompany} />

      <div className={`grid grid-cols-1 ${isGstCompany ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4`}>
        {isGstCompany && (
          <div className="md:col-span-2 card-flat p-6">
            <div className="eyebrow mb-2">Tax Treatment</div>
            <div className="text-sm">
              {intraState ? (
                <span>Intra-state supply · <span className="font-mono">CGST + SGST</span> applied</span>
              ) : (
                <span>Inter-state supply · <span className="font-mono">IGST</span> applied</span>
              )}
            </div>
            <div className="text-[12px] text-muted-foreground mt-1">Company state code: {activeCompany?.state_code || "—"} · Customer state code: {customer?.state_code || "—"}</div>
          </div>
        )}
        {!isGstCompany && (
          <div className="card-flat p-6">
            <div className="eyebrow mb-2">Non-GST Company</div>
            <div className="text-sm text-muted-foreground">This company has no GSTIN registered — quotations are prepared without any GST calculation.</div>
          </div>
        )}
        <div className="card-flat p-6">
          <div className="eyebrow mb-3">Summary</div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{isGstCompany ? "Taxable" : "Subtotal"}</span><span className="font-mono">{inr(totals.subtotal)}</span></div>
            {isGstCompany && (intraState ? (
              <>
                <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="font-mono">{inr(totals.cgst_total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="font-mono">{inr(totals.sgst_total)}</span></div>
              </>
            ) : (
              <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="font-mono">{inr(totals.igst_total)}</span></div>
            ))}
            <div className="pt-2 border-t border-border flex justify-between font-heading font-bold text-base">
              <span>Grand Total</span><span className="font-mono" data-testid="quot-grand-total">{inr(totals.grand_total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="h-11 px-6 text-base" data-testid="quot-save-btn">
          <FloppyDisk size={18} className="mr-1.5" /> {saving ? "Saving…" : "Save Quotation"}
        </Button>
      </div>
    </div>
  );
}
